/**
 * @author      hugo@zumper.com (Hugo Pineda)
 * @copyright   Copyright (c) 2023, Zumper
 */

// https://github.com/chakra-ui/chakra-ui/blob/v1/packages/theme/src/components/menu.ts

import { menuAnatomy as parts } from '@chakra-ui/anatomy'
import type {
  PartsStyleFunction,
  SystemStyleFunction,
  SystemStyleObject,
} from '@chakra-ui/theme-tools'
import { mode } from '@chakra-ui/theme-tools'

const baseStyleList: SystemStyleFunction = (props) => {
  return {
    bg: mode('#fff', 'gray.700')(props),
    boxShadow: mode('sm', 'dark-lg')(props),
    color: 'inherit',
    minW: '3xs',
    py: '2',
    zIndex: 1,
    borderRadius: 'md',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'gray.150',
    overflow: 'hidden',
  }
}

const baseStyleItem: SystemStyleFunction = (props) => {
  return {
    height: 8,
    transitionProperty: 'background',
    transitionDuration: 'ultra-fast',
    transitionTimingFunction: 'ease-in',
    border: 0,
    fontSize: 'sm',
    py: 0,
    bg: 'transparent',
    _focus: {
      bg: mode('gray.50', 'whiteAlpha.100')(props),
    },
    _active: {
      bg: mode('gray.150', 'whiteAlpha.200')(props),
    },
    _expanded: {
      bg: mode('gray.100', 'whiteAlpha.100')(props),
    },
    _disabled: {
      opacity: 0.4,
      cursor: 'not-allowed',
    },
  }
}

const baseStyleGroupTitle: SystemStyleObject = {
  mx: 4,
  my: 2,
  fontWeight: 'semibold',
  fontSize: 'sm',
}

const baseStyleCommand: SystemStyleObject = {
  opacity: 0.6,
}

const baseStyleDivider: SystemStyleObject = {
  border: 0,
  borderBottom: '1px solid',
  borderColor: 'inherit',
  my: '0.5rem',
  opacity: 0.6,
}

const baseStyleButton: SystemStyleObject = {
  transitionProperty: 'common',
  transitionDuration: 'normal',
  fontSize: 'sm',
  height: 8,
  borderRadius: 'md',
  borderStyle: 'solid',
  borderWidth: '1px',
  borderColor: 'gray.350',
  bg: 'gray.0',
  px: 3,
  cursor: 'pointer',
  color: 'gray.800',
  _hover: {
    bg: 'gray.50',
    borderColor: 'gray.600',
  },
  _focus: {
    bg: 'gray.50',
    borderColor: 'neutral.500',
  },
  _active: {
    bg: 'gray.50',
    borderColor: 'gray.950',
    color: 'gray.950',
  },
}

const baseStyle: PartsStyleFunction<typeof parts> = (props) => ({
  button: baseStyleButton,
  list: baseStyleList(props),
  item: baseStyleItem(props),
  groupTitle: baseStyleGroupTitle,
  command: baseStyleCommand,
  divider: baseStyleDivider,
})

const ghostVariant: SystemStyleFunction = (props) => ({
  button: {
    borderColor: 'transparent',
    _hover: {
      borderColor: 'transparent',
    },
    _active: {
      borderColor: 'transparent',
    },
  },
})

const variants = {
  ghost: ghostVariant,
}

export const Z4Menu = {
  parts: parts.keys,
  baseStyle,
  variants,
}
